import { useState, useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { loginApi, googleLoginApi, appleLoginApi } from "../model/authApi";
import {
  validateEmailWithMessage,
  validatePasswordWithMessage,
  validateLoginCredentials,
} from "../utils/auth.utils";
import { loadGoogleScript, triggerGoogleSignIn } from "../utils/googleAuth.utils";
import { loadAppleScript, triggerAppleSignIn } from "../utils/appleAuth.utils";
import { getDeviceId } from "../utils/device.utils";
import { persistToastForNextLoad, toastService } from "@/shared/toast";
import type { LoginCredentials, User } from "../model/authTypes";
import { useAuth } from "@/app/providers/useAuth";
import { routes } from "@/app/navigation/routes";
import { tokenService } from "@/services/auth/token.service";
import { SESSION_PHONE_MOBILE_INPUT_PREFILL } from "../constants/sessionStorageKeys";

/** Typical error JSON shape from user-service login failures. */
type LoginApiErrorBody = {
  message?: string;
  error?: string;
  status?: string;
};

function readLoginApiErrorBody(err: unknown): LoginApiErrorBody | undefined {
  if (!axios.isAxiosError(err)) return undefined;
  const data = err.response?.data;
  if (typeof data !== "object" || data === null) return undefined;
  return data as LoginApiErrorBody;
}

function hasEmailPendingMessage(err: unknown): boolean {
  return readLoginApiErrorBody(err)?.message === "EMAIL_VALIDATION_PENDING";
}

function isForcePasswordUpdateError(err: unknown): boolean {
  const body = readLoginApiErrorBody(err);
  if (!body) return false;
  const marker = "FORCE_PASSWORD_UPDATE";
  return body.message === marker || body.error === marker;
}

function messageFromLoginApiError(err: unknown): string {
  const body = readLoginApiErrorBody(err);
  if (typeof body?.message === "string" && body.message.trim().length > 0) {
    return body.message;
  }
  return "Apple login failed. Please try again.";
}

/**
 * Login ViewModel
 * Manages login form state and business logic
 */
export const useLoginViewModel = () => {
  const APPLE_PENDING_TOKEN_KEY = "apple_pending_identity_token";
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /** AuthLayoutWrapper does not re-render when the token appears in storage, so we must navigate here. */
  // const redirectAfterAuth = useCallback(() => {
  //   if (!tokenService.hasToken()) {
  //     console.error(
  //       "[auth] redirectAfterAuth: auth_token missing; not navigating. Ensure login APIs called persistAuthToken / setToken before redirectAfterAuth."
  //     );
  //     return;
  //   }

  //   const state = location.state as
  //     | (Partial<LoginCredentials> & { from?: string })
  //     | null
  //     | undefined;

  //   // If coming from any page
  //   if (state?.from) {
  //     navigate(state.from, {
  //       replace: true,
  //     });
  //     return;
  //   }

  //   // Default (Navbar login)
  //   navigate(routes.homepage, { replace: true });
  // }, [location.state, navigate]);

  /** AuthLayoutWrapper does not re-render when the token appears in storage, so we must navigate here. */
  // const redirectAfterAuth = useCallback(() => {
  //   if (!tokenService.hasToken()) {
  //     console.error(
  //       "[auth] redirectAfterAuth: auth_token missing; not navigating. Ensure login APIs called persistAuthToken / setToken before redirectAfterAuth."
  //     );
  //     return;
  //   }

  //   // Priority 1: protest flow restoration via sessionStorage.
  //   const savedProtestFlow = sessionStorage.getItem("property_protest_flow");
  //   if (savedProtestFlow) {
  //     try {
  //       const parsedFlow = JSON.parse(savedProtestFlow);
  //       sessionStorage.removeItem("property_protest_flow");
  //       navigate(routes.properties, { replace: true, state: parsedFlow });
  //       return;
  //     } catch (error) {
  //       console.error("Failed to parse property protest flow:", error);
  //       sessionStorage.removeItem("property_protest_flow");
  //     }
  //   }

  //   // Priority 2: return user to the page they came from (navbar/deep-link login).
  //   const fromState = (location.state as { from?: string } | null | undefined)?.from;
  //   if (typeof fromState === "string" && fromState && fromState !== routes.login) {
  //     navigate(fromState, { replace: true });
  //     return;
  //   }

  //   // Priority 3: default landing.
  //   navigate(routes.home, { replace: true });
  // }, [navigate, location.state]);
  /**
   * Same branching as email/password success: intermediate onboarding screens stay on temp/session
   * auth; only persist app session + redirect when `auth_token` exists (set in auth APIs when eligible).
   */

  const redirectAfterAuth = useCallback(() => {
    if (!tokenService.hasToken()) {
      console.error(
        "[auth] redirectAfterAuth: auth_token missing; not navigating. Ensure login APIs called persistAuthToken / setToken before redirectAfterAuth."
      );
      return;
    }

    // Priority 1: protest flow restoration via sessionStorage.
    const savedProtestFlow = sessionStorage.getItem("property_protest_flow");

    if (savedProtestFlow) {
      try {
        const parsedFlow = JSON.parse(savedProtestFlow);

        sessionStorage.removeItem("property_protest_flow");

        navigate(routes.properties, {
          replace: true,
          state: parsedFlow,
        });

        return;
      } catch (error) {
        console.error("Failed to parse property protest flow:", error);

        sessionStorage.removeItem("property_protest_flow");
      }
    }

    // Priority 2: return user to the page they came from.
    const fromState = (location.state as { from?: string } | null | undefined)?.from;

    // If login came from Properties flow, always restore there with state
    if (fromState === routes.properties) {
      navigate(routes.properties, {
        replace: true,
        state: location.state,
      });

      return;
    }

    // Other pages return normally
    if (typeof fromState === "string" && fromState && fromState !== routes.login) {
      navigate(fromState, { replace: true });

      return;
    }

    // Priority 3: default landing.
    navigate(routes.home, { replace: true });
  }, [navigate, location.state]);

  const navigateAfterCredentialLogin = useCallback(
    (userData: User) => {
      sessionStorage.setItem("otpFlow", "login");
      sessionStorage.setItem("email", userData.email || "");
      console.log("userData0090", userData);
      if (userData.force_mobile_update) {
        sessionStorage.setItem("otpFlow", "login");
        sessionStorage.setItem("phone", userData.phone || "");
        const phone = userData.phone?.trim() ?? "";
        if (phone) {
          sessionStorage.setItem(SESSION_PHONE_MOBILE_INPUT_PREFILL, phone);
        }

        navigate(routes.phoneMobileInput);
        return;
      }

      if (userData.force_password_update && userData.auth_provider !== "google") {
        navigate(routes.forcePasswordUpdate, {
          state: { email: userData.email },
        });
        return;
      }

      if (!userData.phone?.trim()) {
        navigate(routes.phoneMobileInput);
        return;
      }

      if (!userData.is_phone_verified) {
        sessionStorage.setItem("phone", userData.phone);
        navigate(routes.phoneVerification, {
          state: location.state,
        });
        return;
      }

      // if (!userData.is_mail_verified) {
      //   navigate(routes.emailVerificationRequired, {
      //     state: { email: userData.email },
      //   });
      //   return;
      // }

      if (!tokenService.hasToken()) {
        console.error(
          "[auth] navigateAfterCredentialLogin: user passed final gates but auth_token is missing after credential APIs — not calling login()/redirect."
        );
        return;
      }

      login(userData);
      redirectAfterAuth();
    },
    [login, location.state, navigate, redirectAfterAuth]
  );

  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: "",
    password: "",
  });
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const BUTTON_ID = "login-button";
  const processedAppleTokenRef = useRef<string | null>(null);
  const showAppleError = useCallback((message: string) => {
    persistToastForNextLoad(message, "error", 8000);
    toastService.show(message, "error", 8000);
  }, []);

  const mutation = useMutation({
    mutationFn: (data: LoginCredentials) => loginApi(data, "inline", BUTTON_ID),
    // onSuccess: (user) => {
    //   // Persisted token is handled in loginApi(); sync user into app state via AuthContext.
    //   sessionStorage.removeItem(APPLE_PENDING_TOKEN_KEY);
    //   login(user);
    //   redirectAfterAuth();
    // },
    onSuccess: (user) => {
      sessionStorage.removeItem(APPLE_PENDING_TOKEN_KEY);

      console.log("user123", user);
      navigateAfterCredentialLogin(user);
    },
    onError: (error: unknown, variables: LoginCredentials) => {
      sessionStorage.removeItem(APPLE_PENDING_TOKEN_KEY);

      // If backend returns a specific message for pending email verification,
      // redirect user to the email verification pending screen instead of staying on login.
      if (hasEmailPendingMessage(error)) {
        navigate(routes.emailVerificationPending, {
          state: { email: credentials.email },
        });
        return;
      }

      // Backend may reject login with 400 + FORCE_PASSWORD_UPDATE when user payload is not returned;
      // use the email from the login attempt (mutation variables).
      if (isForcePasswordUpdateError(error)) {
        const email = variables.email.trim();
        if (email) {
          sessionStorage.setItem("email", email);
        }
        navigate(routes.forcePasswordUpdate, {
          state: { email: variables.email },
        });
        return;
      }
    },
  });

  const googleLoginMutation = useMutation({
    mutationFn: (idToken: string) => {
      const deviceId = getDeviceId();
      return googleLoginApi(
        {
          idToken,
          referral_code: "",
          deviceId,
          role: 2,
        },
        "central"
      );
    },
    onSuccess: (user) => {
      navigateAfterCredentialLogin(user);
    },
  });

  const appleLoginMutation = useMutation({
    mutationFn: (data: {
      identityToken: string;
      user: { name: { firstName: string; lastName: string } } | null;
    }) => {
      const deviceId = getDeviceId();
      return appleLoginApi(
        {
          identityToken: data.identityToken,
          referral_code: " ",
          deviceId,
          role: 2,
          user: data.user || {
            name: {
              firstName: "",
              lastName: "",
            },
          },
        },
        "central"
      );
    },
    onSuccess: (user) => {
      sessionStorage.removeItem(APPLE_PENDING_TOKEN_KEY);
      toastService.show("Signed in successfully", "success", 6000);
      // One frame so success toast can commit before route change (AuthLayoutWrapper does not auto-redirect).
      window.requestAnimationFrame(() => {
        navigateAfterCredentialLogin(user);
      });
    },
    onError: (error: unknown) => {
      sessionStorage.removeItem(APPLE_PENDING_TOKEN_KEY);
      const errorMessage = messageFromLoginApiError(error);
      showAppleError(errorMessage);
    },
  });

  const processAppleIdentityToken = useCallback(
    (identityToken: string, user: { name: { firstName: string; lastName: string } } | null) => {
      if (!identityToken) {
        showAppleError("Apple Sign-In did not return a valid identity token. Please try again.");
        return;
      }

      if (processedAppleTokenRef.current === identityToken) {
        return;
      }
      processedAppleTokenRef.current = identityToken;

      // Persist first so a refresh during callback handling doesn't lose login.
      sessionStorage.setItem(APPLE_PENDING_TOKEN_KEY, identityToken);
      appleLoginMutation.mutate({ identityToken, user });
    },
    [appleLoginMutation, showAppleError]
  );

  useEffect(() => {
    const pendingIdentityToken = sessionStorage.getItem(APPLE_PENDING_TOKEN_KEY);
    if (!pendingIdentityToken) return;
    processAppleIdentityToken(pendingIdentityToken, null);
  }, [processAppleIdentityToken]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const identityToken = searchParams.get("id_token") || hashParams.get("id_token");
    if (!identityToken) return;

    // Strip OAuth params immediately so we don't re-process on re-render / avoid extra history noise.
    const url = new URL(window.location.href);
    url.searchParams.delete("id_token");
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.hash = "";
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);

    processAppleIdentityToken(identityToken, null);
  }, [processAppleIdentityToken]);

  // No need to initialize on mount - we'll initialize when button is clicked

  const updateEmail = useCallback((email: string) => {
    setCredentials((prev) => ({ ...prev, email }));
    // Validate email on change (only show error if field has been touched)
    if (email) {
      const validation = validateEmailWithMessage(email);
      setEmailError(validation.error || null);
    } else {
      setEmailError(null);
    }
  }, []);

  const updatePassword = useCallback((password: string) => {
    setCredentials((prev) => ({ ...prev, password }));
    // Validate password on change (only show error if field has been touched)
    if (password) {
      const validation = validatePasswordWithMessage(password);
      setPasswordError(validation.error || null);
    } else {
      setPasswordError(null);
    }
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate all fields before submission
      const validation = validateLoginCredentials(credentials.email, credentials.password);
      setEmailError(validation.emailError);
      setPasswordError(validation.passwordError);

      if (!validation.isValid) {
        return;
      }

      // Include static role: 1 along with credentials
      mutation.mutate({
        ...credentials,
        role: 2,
      });
    },
    [credentials, mutation]
  );

  const handleForgotPassword = useCallback(() => {
    navigate(routes.forgotPassword, {
      state: { email: credentials.email || undefined },
    });
  }, [credentials.email, navigate]);

  const handleGoogleLogin = useCallback(async () => {
    try {
      // Ensure script is loaded
      await loadGoogleScript();

      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!googleClientId) {
        toastService.show("Google Sign-In is not configured. Please contact support.", "error");
        return;
      }

      // Use button-based approach which doesn't rely on FedCM
      triggerGoogleSignIn(
        googleClientId,
        (idToken) => {
          googleLoginMutation.mutate(idToken);
        },
        (error) => {
          console.error("Google Sign-In error:", error);
          toastService.show(
            "Failed to sign in with Google. Please try again or use email/password.",
            "error"
          );
        }
      );
    } catch (error: unknown) {
      console.error("Google Sign-In initialization error:", error);
      toastService.show("Failed to initialize Google Sign-In. Please try again.", "error");
    }
  }, [googleLoginMutation]);

  const handleAppleLogin = useCallback(async () => {
    try {
      // Ensure script is loaded
      await loadAppleScript();

      const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID;
      if (!appleClientId) {
        showAppleError("Apple Sign-In is not configured. Please contact support.");
        return;
      }

      // Trigger Apple Sign-In
      triggerAppleSignIn(
        appleClientId,
        (identityToken, user) => {
          processAppleIdentityToken(identityToken, user);
        },
        (error) => {
          console.error("Apple Sign-In error:", error);
          showAppleError("Failed to sign in with Apple. Please try again or use email/password.");
        }
      );
    } catch (error: unknown) {
      console.error("Apple Sign-In initialization error:", error);
      showAppleError("Failed to initialize Apple Sign-In. Please try again.");
    }
  }, [processAppleIdentityToken, showAppleError]);

  const reset = useCallback(() => {
    setCredentials({
      email: "",
      password: "",
    });
    setEmailError(null);
    setPasswordError(null);
  }, []);

  return {
    credentials,
    isLoading: mutation.isPending || googleLoginMutation.isPending || appleLoginMutation.isPending,
    error: mutation.error || googleLoginMutation.error || appleLoginMutation.error,
    isSuccess: mutation.isSuccess || googleLoginMutation.isSuccess || appleLoginMutation.isSuccess,
    emailError,
    passwordError,
    updateEmail,
    updatePassword,
    handleSubmit,
    handleGoogleLogin,
    handleAppleLogin,
    handleForgotPassword,
    reset,
  };
};
